import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom';
import SideBar from '../components/SideBar';
import PhoneInput from 'react-phone-input-2';
import Select from 'react-select';
import '../styles/CreateCustomerAccount.css';
import 'react-phone-input-2/lib/style.css';
import { retailerService } from '../services/RetailerServices';
import { toast } from 'react-toastify';
import { API_URL } from '../constant';

function CreateRetailerAccount() {
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [validatingFields, setValidatingFields] = useState(false);
    
    // State for retail types
    const [retailTypesData, setRetailTypesData] = useState({});
    const [loadingRetailTypes, setLoadingRetailTypes] = useState(false);

    // Owner Details
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");

    // Business Details
    const [email, setEmail] = useState("");
    const [mobile, setMobile] = useState("");
    const [ownerPhoto, setOwnerPhoto] = useState("");
    const [photoFile, setPhotoFile] = useState(null);
    const [retailName, setRetailName] = useState("");
    
    // Retail Type & Sub Type (as strings)
    const [retailType, setRetailType] = useState("");
    const [retailSubType, setRetailSubType] = useState("");
    const [retailSubTypeOptions, setRetailSubTypeOptions] = useState([]);
    
    const [shopAddress, setShopAddress] = useState("");
    const [website, setWebsite] = useState("");
    const [signedContract, setSignedContract] = useState(false);
    const [shopStatus, setShopStatus] = useState("active");

    // Business Address
    const [line1, setLine1] = useState("");
    const [line2, setLine2] = useState("");
    const [line3, setLine3] = useState("");
    const [selectedCity, setSelectedCity] = useState("");
    const [postalCode, setPostalCode] = useState("");
    const [selectedCounty, setSelectedCounty] = useState("");
    const [selectedCountry, setSelectedCountry] = useState("");

    // Security
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showModal, setShowModal] = useState(false);
    const [generatedPassword, setGeneratedPassword] = useState("");
    const [confirmCopy, setConfirmCopy] = useState(false);

    // Key Management State
    const [generateKeys, setGenerateKeys] = useState(true);

    // Error states
    const [firstNameError, setFirstNameError] = useState("");
    const [lastNameError, setLastNameError] = useState("");
    const [emailError, setEmailError] = useState("");
    const [mobileError, setMobileError] = useState("");
    const [line1Error, setLine1Error] = useState("");
    const [cityError, setCityError] = useState("");
    const [postalCodeError, setPostalCodeError] = useState("");
    const [retailNameError, setRetailNameError] = useState("");
    const [retailTypeError, setRetailTypeError] = useState("");
    const [retailSubTypeError, setRetailSubTypeError] = useState("");
    const [passwordError, setPasswordError] = useState("");
    const [confirmPasswordError, setConfirmPasswordError] = useState("");
    const [websiteError, setWebsiteError] = useState("");

    // Region options
    const regionOptions = {
        India: [
            "Andhra Pradesh", "Delhi", "Karnataka", "Maharashtra", "Tamil Nadu", "West Bengal",
        ],
        "Sri Lanka": [
            "Central Province", "Eastern Province", "Northern Province", "Southern Province", 
            "Western Province", "North Central Province", "North Western Province", 
            "Sabaragamuwa Province", "Uva Province",
        ],
        Canada: ["Alberta", "British Columbia", "Manitoba", "Ontario", "Quebec"],
        "United Kingdom": [
            "England - Bedfordshire", "England - Berkshire", "England - Bristol", 
            "England - Buckinghamshire", "England - Cambridgeshire", "England - Cheshire",
            "England - City of London", "England - Cornwall", "England - Cumbria",
            "England - Derbyshire", "England - Devon", "England - Dorset",
            "England - Durham", "England - East Riding of Yorkshire", "England - East Sussex",
            "England - Essex", "England - Gloucestershire", "England - Greater London",
            "England - Greater Manchester", "England - Hampshire", "England - Herefordshire",
            "England - Hertfordshire", "England - Isle of Wight", "England - Kent",
            "England - Lancashire", "England - Leicestershire", "England - Lincolnshire",
            "England - Merseyside", "England - Norfolk", "England - North Yorkshire",
            "England - Northamptonshire", "England - Northumberland", "England - Nottinghamshire",
            "England - Oxfordshire", "England - Rutland", "England - Shropshire",
            "England - Somerset", "England - South Yorkshire", "England - Staffordshire",
            "England - Suffolk", "England - Surrey", "England - Sussex",
            "England - Tyne & Wear", "England - Warwickshire", "England - West Midlands",
            "England - West Sussex", "England - West Yorkshire", "England - Wiltshire",
            "England - Worcestershire",
        ],
        Iran: ["Tehran", "Isfahan", "Fars", "Mazandaran", "Khorasan Razavi"],
    };

    // Shop status options
    const shopStatusOptions = [
        { value: "active", label: "Active" },
        { value: "inactive", label: "Inactive" },
    ];

    // Retail type options (will be populated from API)
    const [retailTypeOptions, setRetailTypeOptions] = useState([]);

    // Get current country option for select
    const currentCountryOption = selectedCountry ? { value: selectedCountry, label: selectedCountry } : null;
    // Get current region option for select
    const currentRegionOption = selectedCounty ? { value: selectedCounty, label: selectedCounty } : null;
    // Get current retail type option for select
    const currentRetailTypeOption = retailType ? { value: retailType, label: retailType } : null;
    // Get current retail sub-type option for select
    const currentRetailSubTypeOption = retailSubType ? { value: retailSubType, label: retailSubType } : null;
    // Get current shop status option for select
    const currentShopStatusOption = shopStatusOptions.find(opt => opt.value === shopStatus);

    // Fetch retail types on component mount
    useEffect(() => {
        fetchRetailTypes();
    }, []);

    // Update sub-type options when retail type changes
    useEffect(() => {
        if (retailType && retailTypesData[retailType]) {
            const subTypeOptions = retailTypesData[retailType].map(subType => ({
                value: subType,
                label: subType
            }));
            setRetailSubTypeOptions(subTypeOptions);
            setRetailSubType(""); // Reset sub-type when main type changes
        } else {
            setRetailSubTypeOptions([]);
            setRetailSubType("");
        }
    }, [retailType, retailTypesData]);

    // Fetch retail types from backend
    const fetchRetailTypes = async () => {
        setLoadingRetailTypes(true);
        try {
            const response = await fetch(`${API_URL}/retail-types/registration-data`);
            const data = await response.json();
            
            if (data.success) {
                // Store the grouped data
                setRetailTypesData(data.data);
                
                // Create options for retail type dropdown
                const typeOptions = Object.keys(data.data).map(type => ({
                    value: type,
                    label: type
                }));
                setRetailTypeOptions(typeOptions);
            } else {
                toast.error('Failed to load retail types');
                // Fallback to default options
                setRetailTypesData(fallbackRetailTypesData);
                setRetailTypeOptions(fallbackRetailTypeOptions);
            }
        } catch (error) {
            console.error('Error fetching retail types:', error);
            toast.error('Failed to load retail types');
            // Fallback to default options
            setRetailTypesData(fallbackRetailTypesData);
            setRetailTypeOptions(fallbackRetailTypeOptions);
        } finally {
            setLoadingRetailTypes(false);
        }
    };

    // Fallback data
    const fallbackRetailTypesData = {
        "Clothing": ["Men's Wear", "Women's Wear", "Children's Wear"],
        "Electronics": ["Mobile Phones", "Computers", "Home Appliances"],
        "Grocery": ["Supermarket", "Convenience Store", "Specialty Food"],
        "Furniture": ["Home Furniture", "Office Furniture", "Garden Furniture"],
        "Other": ["Other"]
    };

    const fallbackRetailTypeOptions = Object.keys(fallbackRetailTypesData).map(type => ({
        value: type,
        label: type
    }));

    // Check if email exists
    const checkEmailExists = async (email) => {
        try {
            const response = await fetch(`${API_URL}/check-email?email=${encodeURIComponent(email)}`);
            const data = await response.json();
            return data.exists || false;
        } catch (error) {
            console.error('Error checking email:', error);
            return false;
        }
    };

    // Function to normalize website URL
    const normalizeWebsiteUrl = (url) => {
        if (!url || url.trim() === '') return '';
        
        let normalizedUrl = url.trim();
        
        // Remove any leading/trailing whitespace
        normalizedUrl = normalizedUrl.trim();
        
        // If it starts with www., add https://
        if (normalizedUrl.startsWith('www.')) {
            normalizedUrl = 'https://' + normalizedUrl;
        }
        // If it doesn't start with http:// or https://, add https://
        else if (!normalizedUrl.startsWith('http://') && !normalizedUrl.startsWith('https://')) {
            // Check if it looks like a domain (contains a dot)
            if (normalizedUrl.includes('.') && !normalizedUrl.includes(' ')) {
                normalizedUrl = 'https://' + normalizedUrl;
            }
        }
        
        return normalizedUrl;
    };

    // Function to validate website URL
    const validateWebsiteUrl = (url) => {
        if (!url || url.trim() === '') return { isValid: true, message: "" };
        
        const urlStr = url.trim();
        
        // Basic domain pattern validation
        const domainPattern = /^(https?:\/\/)?(www\.)?[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]\.[a-zA-Z]{2,}(\.[a-zA-Z]{2,})?(\/.*)?$/;
        
        if (!domainPattern.test(urlStr)) {
            return { 
                isValid: false, 
                message: "Please enter a valid website URL (e.g., example.com, www.example.com, https://example.com)" 
            };
        }
        
        return { isValid: true, message: "" };
    };

    const nextStep = async () => {
        if (step === 1) {
            const isValid = await validateOwnerDetails();
            if (!isValid) return;
        }
        if (step === 2) {
            const isValid = await validateBusinessDetails();
            if (!isValid) return;
        }
        if (step === 3) {
            const isValid = validateBusinessAddress();
            if (!isValid) return;
        }
        if (step === 4) {
            const isValid = validateSecurityDetails();
            if (!isValid) return;
        }
        setStep((prev) => Math.min(prev + 1, 5));
    };

    const prevStep = () => setStep((prev) => Math.max(prev - 1, 1));

    const steps = [
        { label: "Owner Details", sub: "Enter owner name" },
        { label: "Business Details", sub: "Enter business information" },
        { label: "Business Address", sub: "Enter business address" },
        { label: "Security", sub: "Set account password" },
        { label: "Key Management", sub: "Manage cryptographic keys" },
    ];

    const validatePassword = (pwd) => {
        const minLength = /.{8,}/;
        const upper = /[A-Z]/;
        const lower = /[a-z]/;
        const number = /[0-9]/;
        const special = /[!@#$%^&*(),.?":{}|<>]/;

        if (!pwd) return "Password is required.";
        if (!minLength.test(pwd)) return "Password must be at least 8 characters long.";
        if (!upper.test(pwd)) return "Password must include an uppercase letter.";
        if (!lower.test(pwd)) return "Password must include a lowercase letter.";
        if (!number.test(pwd)) return "Password must include a number.";
        if (!special.test(pwd)) return "Password must include a special character.";
        return "";
    };

    const generatePassword = () => {
        const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()";
        const randomPass = Array.from({ length: 10 }, () =>
            chars.charAt(Math.floor(Math.random() * chars.length))
        ).join("");

        setGeneratedPassword(randomPass);
        setConfirmCopy(false);
        setShowModal(true);
    };

    const handleCreateAccount = async () => {
        if (!validateAllSteps()) {
            toast.error("Please fix validation errors before creating account.");
            return;
        }

        setLoading(true);
        try {
            // Prepare form data for file upload
            const formData = new FormData();
            
            // Add owner personal data
            formData.append('first_name', firstName);
            formData.append('last_name', lastName);
            
            // Add business contact data
            formData.append('email', email);
            formData.append('phone_number', mobile);
            formData.append('password', password);
            formData.append('password_confirmation', confirmPassword);
            
            // Add business address
            formData.append('address_line1', line1);
            formData.append('address_line2', line2 || '');
            formData.append('address_line3', line3 || '');
            formData.append('city', selectedCity);
            formData.append('postcode', postalCode);
            
            // Add country data
            if (selectedCountry) {
                formData.append('country_name', selectedCountry);
                formData.append('region', selectedCounty);
            }
            
            // Add business details
            formData.append('retail_name', retailName);
            formData.append('retail_type', retailType); // Store as string
            formData.append('retail_sub_type', retailSubType); // Store as string
            formData.append('retail_address', shopAddress || `${line1}${line2 ? ', ' + line2 : ''}${line3 ? ', ' + line3 : ''}, ${selectedCity}, ${selectedCountry}`);
            
            // Normalize website URL before sending
            const normalizedWebsite = website ? normalizeWebsiteUrl(website) : '';
            if (normalizedWebsite) {
                formData.append('website', normalizedWebsite);
            }
            
            formData.append('signed_contract', signedContract ? '1' : '0');
            formData.append('shop_status', shopStatus);

            // Add key generation preference
            formData.append('generate_keys', generateKeys ? '1' : '0');
            
            // Add photo if uploaded
            if (photoFile) {
                formData.append('photo', photoFile);
            }

            // Call the API to create retailer
            const response = await retailerService.createRetailer(formData);
            
            toast.success('Retailer account created successfully!');
            
            // Navigate to manage retailer page
            navigate("/manage-retailer");
            
        } catch (error) {
            console.error('Create account failed:', error);
            const errorMessage = error.response?.data?.message || 
                               error.response?.data?.error || 
                               'Failed to create retailer account';
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const validateAllSteps = () => {
        const step1Valid = validateOwnerDetailsSync();
        const step2Valid = validateBusinessDetailsSync();
        const step3Valid = validateBusinessAddress();
        const step4Valid = validateSecurityDetails();
        
        return step1Valid && step2Valid && step3Valid && step4Valid;
    };

    // Synchronous validation for step 1 (without API calls)
    const validateOwnerDetailsSync = () => {
        let valid = true;

        // First name validation
        if (!firstName.trim()) {
            setFirstNameError("First name is required.");
            valid = false;
        } else if (!/^[A-Za-z\s]+$/.test(firstName)) {
            setFirstNameError("First name must contain only letters.");
            valid = false;
        } else if (firstName.trim().length < 3) {
            setFirstNameError("First name must be at least 3 letters long.");
            valid = false;
        } else {
            setFirstNameError("");
        }

        // Last name validation
        if (!lastName.trim()) {
            setLastNameError("Last name is required.");
            valid = false;
        } else if (!/^[A-Za-z\s]+$/.test(lastName)) {
            setLastNameError("Last name must contain only letters.");
            valid = false;
        } else if (lastName.trim().length < 3) {
            setLastNameError("Last name must be at least 3 letters long.");
            valid = false;
        } else {
            setLastNameError("");
        }

        return valid;
    };

    // Async validation for step 1 (with API calls)
    const validateOwnerDetails = async () => {
        // First do basic validation
        if (!validateOwnerDetailsSync()) {
            return false;
        }

        return true;
    };

    // Synchronous validation for step 2 (without API calls)
    const validateBusinessDetailsSync = () => {
        let valid = true;

        // Email validation
        if (!email.trim()) {
            setEmailError("Business email is required.");
            valid = false;
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            setEmailError("Please enter a valid email address.");
            valid = false;
        } else {
            setEmailError("");
        }

        // Phone validation
        if (!mobile.trim()) {
            setMobileError("Business mobile number is required");
            valid = false;
        } else {
            const digitsOnly = mobile.replace(/\D/g, "");
            const match = digitsOnly.match(/^94|^91|^44|^1|^33|^49|^61|^65|^81/);
            let nationalNumber = digitsOnly;
            if (match) {
                nationalNumber = digitsOnly.slice(match[0].length);
            }
            
            if (nationalNumber.length > 10) {
                setMobileError("Mobile number must not exceed 10 digits (excluding country code).");
                valid = false;
            } else {
                setMobileError("");
            }
        }

        // Business name validation
        if (!retailName.trim()) {
            setRetailNameError("Business name is required.");
            valid = false;
        } else {
            setRetailNameError("");
        }

        // Retail Type validation
        if (!retailType.trim()) {
            setRetailTypeError("Retail type is required.");
            valid = false;
        } else {
            setRetailTypeError("");
        }

        // Retail Sub Type validation
        if (!retailSubType.trim()) {
            setRetailSubTypeError("Retail sub-type is required.");
            valid = false;
        } else {
            setRetailSubTypeError("");
        }

        // Website validation (only if provided)
        if (website.trim()) {
            const validation = validateWebsiteUrl(website);
            if (!validation.isValid) {
                setWebsiteError(validation.message);
                valid = false;
            } else {
                setWebsiteError("");
            }
        } else {
            setWebsiteError("");
        }

        return valid;
    };

    // Async validation for step 2 (with API calls)
    const validateBusinessDetails = async () => {
        // First do basic validation
        if (!validateBusinessDetailsSync()) {
            return false;
        }

        setValidatingFields(true);
        
        try {
            // Check email existence
            const emailExists = await checkEmailExists(email);
            
            let hasErrors = false;

            if (emailExists) {
                setEmailError("This email is already registered. Please use a different email.");
                hasErrors = true;
            }

            if (hasErrors) {
                toast.error("Email already exists. Please use different credentials.");
                return false;
            }

            return true;
            
        } catch (error) {
            toast.error("Error checking availability. Please try again.");
            return false;
        } finally {
            setValidatingFields(false);
        }
    };

    const validateBusinessAddress = () => {
        let valid = true;

        if (!line1.trim()) {
            setLine1Error("Address line 1 is required.");
            valid = false;
        } else {
            setLine1Error("");
        }

        if (!selectedCity.trim()) {
            setCityError("City is required.");
            valid = false;
        } else {
            setCityError("");
        }

        // Postal code validation
        if (!postalCode.trim()) {
            setPostalCodeError("Postal code is required.");
            valid = false;
        } else if (!/^[A-Za-z0-9\s\-]+$/.test(postalCode)) {
            setPostalCodeError("Please enter a valid postal code.");
            valid = false;
        } else {
            setPostalCodeError("");
        }

        return valid;
    };

    const validateSecurityDetails = () => {
        let valid = true;

        const passwordValidationError = validatePassword(password);
        if (passwordValidationError) {
            setPasswordError(passwordValidationError);
            valid = false;
        } else {
            setPasswordError("");
        }

        if (!confirmPassword.trim()) {
            setConfirmPasswordError("Please confirm your password.");
            valid = false;
        } else if (password !== confirmPassword) {
            setConfirmPasswordError("Passwords do not match.");
            valid = false;
        } else {
            setConfirmPasswordError("");
        }

        return valid;
    };

    const handlePhotoUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            // Validate file type and size
            const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/avif', 'image/webp'];
            if (!validTypes.includes(file.type)) {
                toast.error('Please select a valid image file (JPEG, PNG, JPG, GIF, AVIF, WEBP)');
                return;
            }
            
            if (file.size > 2 * 1024 * 1024) { // 2MB
                toast.error('Image size should be less than 2MB');
                return;
            }
            
            setPhotoFile(file);
            const reader = new FileReader();
            reader.onload = (e) => {
                setOwnerPhoto(e.target.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleCancel = () => {
        navigate("/manage-retailer");
    };

    // Format website for display (show without protocol if it's https://)
    const formatWebsiteForDisplay = (url) => {
        if (!url) return '';
        
        // Remove https:// if present
        if (url.startsWith('https://')) {
            return url.replace('https://', '');
        }
        // Remove http:// if present
        if (url.startsWith('http://')) {
            return url.replace('http://', '');
        }
        
        return url;
    };

    // Handle website input change
    const handleWebsiteChange = (value) => {
        setWebsite(value);
        if (websiteError) setWebsiteError("");
    };

    return (
        <div className="member-details-layout">
            <SideBar />
    
            <div className="member-details-main">
                {/* Header */}
                <div className="edit-header">
                    <h2 style={{width:"400px"}}>Create Retailer Shop Account</h2>
                    <p>After creating, Retailer will receive login credentials via email</p>
                </div>
    
                {/* Breadcrumb */}
                <div className="breadcrumb">
                    <Link to="/dashboard" className="breadcrumb-link">Home</Link>
                    <span className="breadcrumb-separator">{">"}</span>
                    <Link to="/manage-retailer" className="breadcrumb-link">Manage Retail Business</Link>
                    <span className="breadcrumb-separator">{">"}</span>
                    <span className="breadcrumb-current">Create Retailer Shop Account</span>
                </div>

                <div className="multi-form-container">
                    <div className="progress-bar">
                        {steps.map((item, i) => {
                            const stepNum = i + 1;
                            const isActive = step === stepNum;
                            const isCompleted = step > stepNum;
                            return (
                                <div key={i} className="progress-step">
                                    <div
                                        className={`circle ${
                                            isCompleted ? "completed" : isActive ? "active" : ""
                                        }`}
                                    >
                                        {isCompleted ? "✓" : stepNum}
                                    </div>
                                    <div className="step-text">
                                        <p className={`step-label ${isActive ? "active-label" : ""}`}>
                                            {item.label}
                                        </p>
                                        <p className={`step-subtext ${isActive ? "active-sub" : ""}`}>
                                            {item.sub}
                                        </p>
                                    </div>
                                    {i < steps.length - 1 && (
                                        <div
                                            className={`line ${
                                                isCompleted ? "line-filled" : "line-empty"
                                            }`}
                                        ></div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* Step content */}
                    <div className="step-content">
                        {/* Step 1: Owner Details */}
                        {step === 1 && (
                            <div>
                                <h3>Owner Details</h3>
                                <hr/>
                                <div className="form-grid">
                                    <div className="form-group">
                                        <label>
                                            First Name <span className="required">*</span>
                                        </label>
                                        <input 
                                            type="text" 
                                            value={firstName}
                                            onChange={(e) => {
                                                setFirstName(e.target.value);
                                                if (firstNameError) setFirstNameError("");
                                            }}
                                            placeholder="Enter first name" 
                                            disabled={loading || validatingFields}
                                        />
                                        <div className="error-container">
                                            {firstNameError && <p className="error-text">{firstNameError}</p>}
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label>
                                            Last Name <span className="required">*</span>
                                        </label>
                                        <input 
                                            type="text" 
                                            value={lastName}
                                            onChange={(e) => {
                                                setLastName(e.target.value);
                                                if (lastNameError) setLastNameError("");
                                            }}
                                            placeholder="Enter last name" 
                                            disabled={loading || validatingFields}
                                        />
                                        <div className="error-container">
                                            {lastNameError && <p className="error-text">{lastNameError}</p>}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Step 2: Business Details */}
                        {step === 2 && (
                            <div>
                                <h3>Business Details</h3>
                                <hr/>
                                <div className="form-grid">
                                    <div className="form-group full-width">
                                        <label>Business Logo</label>
                                        <div className="photo-upload-container">
                                            {ownerPhoto && (
                                                <img 
                                                    src={ownerPhoto} 
                                                    alt="Business Logo" 
                                                    className="owner-photo-preview"
                                                />
                                            )}
                                            <input 
                                                type="file" 
                                                accept="image/*"
                                                onChange={handlePhotoUpload}
                                                className="photo-upload-input"
                                                disabled={loading || validatingFields || loadingRetailTypes}
                                            />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label>
                                            Business Name <span className="required">*</span>
                                        </label>
                                        <input 
                                            type="text" 
                                            value={retailName}
                                            onChange={(e) => {
                                                setRetailName(e.target.value);
                                                if (retailNameError) setRetailNameError("");
                                            }}
                                            placeholder="Enter business name" 
                                            disabled={loading || loadingRetailTypes}
                                        />
                                        <div className="error-container">
                                            {retailNameError && <p className="error-text">{retailNameError}</p>}
                                        </div>
                                    </div>
                                    
                                    {/* Retail Type Dropdown */}
                                    <div className="form-group">
                                        <label>
                                            Retail Type <span className="required">*</span>
                                        </label>
                                        <Select
                                            options={retailTypeOptions}
                                            value={currentRetailTypeOption}
                                            onChange={(option) => {
                                                setRetailType(option.value);
                                                if (retailTypeError) setRetailTypeError("");
                                            }}
                                            placeholder={loadingRetailTypes ? "Loading retail types..." : "Select retail type"}
                                            isSearchable
                                            classNamePrefix="custom-select"
                                            isDisabled={loading || loadingRetailTypes}
                                            isLoading={loadingRetailTypes}
                                            styles={{
                                                control: (base, state) => ({
                                                    ...base,
                                                    border: retailTypeError ? '1.17px solid #dc3545' : '1.17px solid rgba(209, 209, 209, 1)',
                                                    borderRadius: '17.6px',
                                                    background: 'rgba(253, 253, 253, 1)',
                                                    minHeight: '38px',
                                                    boxShadow: state.isFocused ? 'none' : 'none',
                                                    '&:hover': 'none',
                                                    fontSize: '14px',
                                                    color:'rgba(105, 105, 105, 1)',
                                                }),
                                                valueContainer: (base) => ({
                                                    ...base,
                                                    padding: '0 8px',
                                                }),
                                                singleValue: (base) => ({
                                                    ...base,
                                                    color:'black',
                                                }),
                                                placeholder: (base) => ({
                                                    ...base,
                                                    color:'rgba(105, 105, 105, 1)',
                                                }),
                                                menu: (base) => ({
                                                    ...base,
                                                    backgroundColor: '#fff',
                                                    borderRadius: '6px',
                                                    marginTop: '2px',
                                                    zIndex: 9999,
                                                }),
                                                option: (base, { isSelected, isFocused }) => ({
                                                    ...base,
                                                    backgroundColor: isSelected
                                                        ? '#e6f0ff'
                                                        : isFocused
                                                        ? '#e6f0ff'
                                                        : '#fff',
                                                    color: isSelected ? '#000' : '#000',
                                                    cursor: 'pointer',
                                                }),
                                            }}
                                        />
                                        <div className="error-container">
                                            {retailTypeError && <p className="error-text">{retailTypeError}</p>}
                                        </div>
                                    </div>
                                    
                                    {/* Retail Sub Type Dropdown */}
                                    <div className="form-group">
                                        <label>
                                            Retail Sub Type <span className="required">*</span>
                                        </label>
                                        <Select
                                            options={retailSubTypeOptions}
                                            value={currentRetailSubTypeOption}
                                            onChange={(option) => {
                                                setRetailSubType(option.value);
                                                if (retailSubTypeError) setRetailSubTypeError("");
                                            }}
                                            placeholder={retailType ? "Select sub type" : "First select retail type"}
                                            isSearchable
                                            classNamePrefix="custom-select"
                                            isDisabled={loading || !retailType || loadingRetailTypes}
                                            styles={{
                                                control: (base, state) => ({
                                                    ...base,
                                                    border: retailSubTypeError ? '1.17px solid #dc3545' : '1.17px solid rgba(209, 209, 209, 1)',
                                                    borderRadius: '17.6px',
                                                    background: 'rgba(253, 253, 253, 1)',
                                                    minHeight: '38px',
                                                    boxShadow: state.isFocused ? 'none' : 'none',
                                                    '&:hover': 'none',
                                                    fontSize: '14px',
                                                    color:'rgba(105, 105, 105, 1)',
                                                }),
                                                valueContainer: (base) => ({
                                                    ...base,
                                                    padding: '0 8px',
                                                }),
                                                singleValue: (base) => ({
                                                    ...base,
                                                    color:'black',
                                                }),
                                                placeholder: (base) => ({
                                                    ...base,
                                                    color:'rgba(105, 105, 105, 1)',
                                                }),
                                                menu: (base) => ({
                                                    ...base,
                                                    backgroundColor: '#fff',
                                                    borderRadius: '6px',
                                                    marginTop: '2px',
                                                    zIndex: 9999,
                                                }),
                                                option: (base, { isSelected, isFocused }) => ({
                                                    ...base,
                                                    backgroundColor: isSelected
                                                        ? '#e6f0ff'
                                                        : isFocused
                                                        ? '#e6f0ff'
                                                        : '#fff',
                                                    color: isSelected ? '#000' : '#000',
                                                    cursor: 'pointer',
                                                }),
                                            }}
                                        />
                                        <div className="error-container">
                                            {retailSubTypeError && <p className="error-text">{retailSubTypeError}</p>}
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label>
                                            Business Email <span className="required">*</span>
                                        </label>
                                        <input 
                                            type="email" 
                                            value={email}
                                            onChange={(e) => {
                                                setEmail(e.target.value);
                                                if (emailError) setEmailError("");
                                            }}
                                            placeholder="Enter business email" 
                                            disabled={loading || validatingFields}
                                        />
                                        <div className="error-container">
                                            {emailError && <p className="error-text">{emailError}</p>}
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label>
                                            Business Mobile <span className="required">*</span>
                                        </label>
                                        <div className="phone-input-container">
                                            <PhoneInput
                                                country={"gb"}
                                                value={mobile}
                                                onChange={(value, country) => {
                                                    const digitsOnly = value.replace(/\D/g, "");
                                                    const dialCode = country?.dialCode || "";
                                                    const dialCodeLength = dialCode.length;
                                                    let nationalNumber = digitsOnly.slice(dialCodeLength);

                                                    if (nationalNumber.length > 10) {
                                                        nationalNumber = nationalNumber.slice(0, 10);
                                                    }

                                                    const finalValue = `+${dialCode}${nationalNumber}`;
                                                    setMobile(finalValue);
                                                    
                                                    if (mobileError) setMobileError("");
                                                }}
                                                onKeyDown={(e) => {
                                                    const digitsOnly = mobile.replace(/\D/g, "");
                                                    const dialCode = mobile.match(/^\+(\d{1,3})/)?.[1] || "";
                                                    const nationalNumber = digitsOnly.slice(dialCode.length);

                                                    if (nationalNumber.length >= 10 && /[0-9]/.test(e.key)) {
                                                        e.preventDefault();
                                                    }
                                                }}
                                                inputProps={{
                                                    name: "mobile",
                                                    required: true,
                                                    disabled: loading || validatingFields,
                                                }}
                                                inputClass="phone-input"
                                                containerClass="phone-container"
                                            />
                                        </div>
                                        <div className="error-container">
                                            {mobileError && <p className="error-text">{mobileError}</p>}
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label>Website</label>
                                        <input 
                                            type="text" 
                                            value={formatWebsiteForDisplay(website)}
                                            onChange={(e) => handleWebsiteChange(e.target.value)}
                                            placeholder="Enter website (e.g., example.com, www.example.com)" 
                                            disabled={loading}
                                        />
                                        <div className="error-container">
                                            {websiteError && <p className="error-text">{websiteError}</p>}
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label>Contract Signed</label>
                                        <div className="checkbox-container">
                                            <input 
                                                type="checkbox" 
                                                checked={signedContract}
                                                onChange={(e) => setSignedContract(e.target.checked)}
                                                className="form-checkbox"
                                                disabled={loading}
                                            />
                                            <span>Yes</span>
                                        </div>
                                        <div className="error-container"/>
                                    </div>
                                    <div className="form-group">
                                        <label>Business Status</label>
                                        <Select
                                            options={shopStatusOptions}
                                            value={currentShopStatusOption}
                                            onChange={(option) => setShopStatus(option.value)}
                                            placeholder="Select business status"
                                            classNamePrefix="custom-select"
                                            isDisabled={loading}
                                            styles={{
                                                control: (base, state) => ({
                                                    ...base,
                                                    border: '1.17px solid rgba(209, 209, 209, 1)',
                                                    borderRadius: '17.6px',
                                                    background: 'rgba(253, 253, 253, 1)',
                                                    minHeight: '38px',
                                                    boxShadow: state.isFocused ? 'none' : 'none',
                                                    '&:hover': 'none',
                                                    fontSize: '14px',
                                                    color:'rgba(105, 105, 105, 1)',
                                                }),
                                                valueContainer: (base) => ({
                                                    ...base,
                                                    padding: '0 8px',
                                                }),
                                                singleValue: (base) => ({
                                                    ...base,
                                                    color:'black',
                                                }),
                                                placeholder: (base) => ({
                                                    ...base,
                                                    color:'rgba(105, 105, 105, 1)',
                                                }),
                                                menu: (base) => ({
                                                    ...base,
                                                    backgroundColor: '#fff',
                                                    borderRadius: '6px',
                                                    marginTop: '2px',
                                                    zIndex: 9999,
                                                }),
                                                option: (base, { isSelected, isFocused }) => ({
                                                    ...base,
                                                    backgroundColor: isSelected
                                                        ? '#e6f0ff'
                                                        : isFocused
                                                        ? '#e6f0ff'
                                                        : '#fff',
                                                    color: isSelected ? '#000' : '#000',
                                                    cursor: 'pointer',
                                                }),
                                            }}
                                        />
                                        <div className="error-container"/>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Step 3: Business Address */}
                        {step === 3 && (
                            <div>
                                <h3>Business Address</h3>
                                <hr />
                                <div className="form-grid">
                                    {/* Row 1: Address line 1 and Address line 2 side by side */}
                                    <div className="form-group">
                                        <label>Address line 1 <span className="required">*</span></label>
                                        <input 
                                            type="text" 
                                            value={line1}
                                            onChange={(e) => {
                                                setLine1(e.target.value);
                                                if (line1Error) setLine1Error("");
                                            }}
                                            placeholder="Enter address line 1" 
                                            disabled={loading}
                                        />
                                        <div className="error-container">
                                            {line1Error && <p className="error-text">{line1Error}</p>}
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label>Address line 2</label>
                                        <input 
                                            type="text" 
                                            value={line2}
                                            onChange={(e) => setLine2(e.target.value)} 
                                            placeholder="Enter address line 2" 
                                            disabled={loading}
                                        />
                                        <div className="error-container"/>
                                    </div>

                                    {/* Row 2: Address line 3 - full width */}
                                    <div className="form-group full-width">
                                        <label>Address line 3</label>
                                        <input 
                                            type="text" 
                                            value={line3}
                                            onChange={(e) => setLine3(e.target.value)}
                                            placeholder="Enter address line 3" 
                                            disabled={loading}
                                        />
                                        <div className="error-container"/>
                                    </div>

                                    {/* Row 3: City and Region side by side */}
                                    <div className="form-group">
                                        <label>City <span className="required">*</span></label>
                                        <input
                                            type="text"
                                            value={selectedCity}
                                            onChange={(e) => {
                                                setSelectedCity(e.target.value);
                                                if (cityError) setCityError("");
                                            }}
                                            placeholder="Enter city"
                                            disabled={loading}
                                        />
                                        <div className="error-container">
                                            {cityError && <p className="error-text">{cityError}</p>}
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label>Region <span className="required">*</span></label>
                                        <Select
                                            options={selectedCountry ? regionOptions[selectedCountry]?.map(r => ({ value: r, label: r })) || [] : []}
                                            value={currentRegionOption}
                                            onChange={(option) => setSelectedCounty(option.value)}
                                            placeholder="Select or type region"
                                            isSearchable
                                            classNamePrefix="custom-select"
                                            isDisabled={loading}
                                            styles={{
                                                control: (base, state) => ({
                                                    ...base,
                                                    border: '1.17px solid rgba(209, 209, 209, 1)',
                                                    borderRadius: '17.6px',
                                                    background: 'rgba(253, 253, 253, 1)',
                                                    minHeight: '38px',
                                                    boxShadow: state.isFocused ? 'none' : 'none',
                                                    '&:hover': 'none',
                                                    fontSize: '14px',
                                                    color:'rgba(105, 105, 105, 1)',
                                                }),
                                                valueContainer: (base) => ({
                                                    ...base,
                                                    padding: '0 8px',
                                                }),
                                                singleValue: (base) => ({
                                                    ...base,
                                                    color:'black',
                                                }),
                                                placeholder: (base) => ({
                                                    ...base,
                                                    color:'rgba(105, 105, 105, 1)',
                                                }),
                                                menu: (base) => ({
                                                    ...base,
                                                    backgroundColor: '#fff',
                                                    borderRadius: '6px',
                                                    marginTop: '2px',
                                                    zIndex: 9999,
                                                }),
                                                option: (base, { isSelected, isFocused }) => ({
                                                    ...base,
                                                    backgroundColor: isSelected
                                                        ? '#e6f0ff'
                                                        : isFocused
                                                        ? '#e6f0ff'
                                                        : '#fff',
                                                    color: isSelected ? '#000' : '#000',
                                                    cursor: 'pointer',
                                                }),
                                            }}
                                        />
                                        <div className="error-container"/>
                                    </div>

                                    {/* Row 4: Post Code and Country side by side */}
                                    <div className="form-group">
                                        <label>Post Code <span className="required">*</span></label>
                                        <input
                                            type="text"
                                            value={postalCode}
                                            onChange={(e) => {
                                                setPostalCode(e.target.value);
                                                if (postalCodeError) setPostalCodeError("");
                                            }}
                                            placeholder="Enter postal code"
                                            disabled={loading}
                                        />
                                        <div className="error-container">
                                            {postalCodeError && <p className="error-text">{postalCodeError}</p>}
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label>Country <span className="required">*</span></label>
                                        <Select
                                            options={Object.keys(regionOptions).map(c => ({ value: c, label: c }))}
                                            value={currentCountryOption}
                                            onChange={(option) => {
                                                setSelectedCountry(option.value);
                                                setSelectedCounty("");  // reset region
                                                // Auto-select first region if available
                                                const regions = regionOptions[option.value];
                                                if (regions && regions.length > 0) {
                                                    setSelectedCounty(regions[0]);
                                                }
                                            }}
                                            placeholder="Select or type country"
                                            isSearchable
                                            classNamePrefix="custom-select"
                                            isDisabled={loading}
                                            styles={{
                                                control: (base, state) => ({
                                                    ...base,
                                                    border: '1.17px solid rgba(209, 209, 209, 1)',
                                                    borderRadius: '17.6px',
                                                    background: 'rgba(253, 253, 253, 1)',
                                                    minHeight: '38px',
                                                    boxShadow: state.isFocused ? 'none' : 'none',
                                                    '&:hover': 'none',
                                                    fontSize: '14px',
                                                    color:'rgba(105, 105, 105, 1)',
                                                }),
                                                valueContainer: (base) => ({
                                                    ...base,
                                                    padding: '0 8px',
                                                }),
                                                singleValue: (base) => ({
                                                    ...base,
                                                    color:'black',
                                                }),
                                                placeholder: (base) => ({
                                                    ...base,
                                                    color:'rgba(105, 105, 105, 1)',
                                                }),
                                                menu: (base) => ({
                                                    ...base,
                                                    backgroundColor: '#fff',
                                                    borderRadius: '6px',
                                                    marginTop: '2px',
                                                    zIndex: 9999,
                                                }),
                                                option: (base, { isSelected, isFocused }) => ({
                                                    ...base,
                                                    backgroundColor: isSelected
                                                        ? '#e6f0ff'
                                                        : isFocused
                                                        ? '#e6f0ff'
                                                        : '#fff',
                                                    color: isSelected ? '#000' : '#000',
                                                    cursor: 'pointer',
                                                }),
                                            }}
                                        />
                                        <div className="error-container"/>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Step 4: Security */}
                        {step === 4 && (
                            <div>
                                <h3>Security</h3>
                                <hr />
                                <div className="form-grid">
                                    <div className="form-group password-group">
                                        <label>
                                            Password <span className="required">*</span>
                                        </label>
                                        <div className="password-input-wrapper">
                                            <div className="password-input-column">
                                                <input
                                                    type="password"
                                                    value={password}
                                                    onChange={(e) => {
                                                        const value = e.target.value;
                                                        setPassword(value);
                                                        setPasswordError(validatePassword(value));
                                                    }}
                                                    placeholder="Enter password"
                                                    disabled={loading}
                                                />
                                                <div className="error-container">
                                                    {passwordError && (
                                                        <p className="error-text">{passwordError}</p>
                                                    )}
                                                </div>
                                            </div>

                                            <button
                                                type="button"
                                                className="text-generate-btn"
                                                onClick={generatePassword}
                                                disabled={loading}
                                            >
                                                Generate password
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <div className="form-group confirm-password-group">
                                    <label>
                                        Confirm Password <span className="required">*</span>
                                    </label>
                                    <input
                                        type="password"
                                        value={confirmPassword}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            setConfirmPassword(value);
                                            setConfirmPasswordError(
                                                value !== password ? "Passwords do not match." : ""
                                            );
                                        }}
                                        placeholder="Confirm password"
                                        disabled={loading}
                                    />
                                    <div className="error-container">
                                        {confirmPasswordError && (
                                            <p className="error-text confirm-error-text">
                                                {confirmPasswordError}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Step 5: Key Management */}
                        {step === 5 && (
                            <div>
                                <h3>Key Management</h3>
                                <hr/>
                                <div className="form-grid center-step">
                                    <div className="form-group full-width">
                                        <label>Generate Cryptographic Keys</label>
                                        <div className="checkbox-container">
                                            <input 
                                                type="checkbox" 
                                                checked={generateKeys}
                                                onChange={(e) => setGenerateKeys(e.target.checked)}
                                                className="form-checkbox"
                                                disabled={loading}
                                            />
                                            <span>Generate Public Key and Private Key</span>
                                        </div>
                                        <div className="key-info">
                                            {/* <small>
                                                Recommended: Generate cryptographic keys to enable secure data encryption and digital signatures for this retailer account.
                                                Keys will be automatically generated and securely stored.
                                            </small> */}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Navigation Buttons */}
                    <div className="btn-row">
                        {step > 1 && (
                            <button 
                                className="btn prev" 
                                onClick={prevStep}
                                disabled={loading || validatingFields || loadingRetailTypes}
                            >
                                Previous
                            </button>
                        )}
                        {step < 5 && (
                            <button 
                                className="btn next" 
                                onClick={nextStep}
                                disabled={loading || validatingFields || loadingRetailTypes}
                            >
                                {validatingFields ? "Checking..." : "Next"}
                            </button>
                        )}
                        {step === 5 && (
                            <button 
                                className="btn next" 
                                onClick={handleCreateAccount}
                                disabled={loading || validatingFields || loadingRetailTypes}
                            >
                                {loading ? 'Creating...' : 'Create Account'}
                            </button>
                        )}
                    </div>
                </div>

                {/* Password Generator Modal */}
                {showModal && (
                    <div className="password-modal-overlay">
                        <div className="password-modal">
                            <button
                                className="modal-close"
                                onClick={() => setShowModal(false)}
                            >
                                ×
                            </button>

                            <h3 className="modal-title">Password Generator</h3>

                            <div className="password-box">
                                <input
                                    type="text"
                                    value={generatedPassword}
                                    readOnly
                                    className="generated-password-input"
                                />
                                <button className="regen-btn" onClick={generatePassword}>
                                    Re-generate
                                </button>
                            </div>

                            <div className="checkbox-area">
                                <input
                                    type="checkbox"
                                    id="confirmCopy"
                                    checked={confirmCopy}
                                    onChange={(e) => setConfirmCopy(e.target.checked)}
                                />
                                <label htmlFor="confirmCopy">
                                    You must tick the box after copy the password
                                </label>
                            </div>

                            <button
                                className="use-password-btn"
                                disabled={!confirmCopy}
                                onClick={() => {
                                    setPassword(generatedPassword);
                                    setConfirmPassword(generatedPassword);
                                    setPasswordError("");
                                    setConfirmPasswordError("");
                                    setShowModal(false);
                                }}
                            >
                                Use password
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>      
    );
}

export default CreateRetailerAccount;
